import axios from "axios";
import classNames from 'classnames';
import _ from "lodash";
import React, { useContext, useEffect, useState } from "react";
import { FaFlag } from "react-icons/fa";
import { toast } from "react-toastify";
import {
   Button, ButtonGroup, Card, CardBody, Col, Collapse, Input,
   Modal, ModalBody, ModalFooter, ModalHeader, Row, Table,
} from "reactstrap";
import { PODropdownMenu, ServiceThreadSpinner, SortHeader } from ".";
import { SpinnerButton } from './';
import { useGetPOReviewDetails } from "../hooks";
import { api, constants, dateHelpers, helpers, UserContext } from "../utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export default function InventoryDashBoardPurchasingActivityGrid(props) {
   const {
      selectedItem,
      typeOfDataSource,
      purchActivitySortDirection,
      purchActivitySortField,
   } = props.params;
   const userCtx = useContext(UserContext);
   const { currentUser } = userCtx;

   const [addReview, setAddReview] = useState(false);
   const blankState = {
      datum: null,
      showing: false,
      blankPoReview: { note: "", nextFollowUp: "", reviewId: 0 },
   };
   const [data, setData] = useState([]);
   const [exporting, setExport] = useState(false);
   const [editCopy, setEditCopy] = useState(null);
   const [editIndex, setEditIndex] = useState(null);
   const [loaded, setLoaded] = useState(false);
   const [params, setParams] = useState({});
   const [showModal, setShowModal] = useState(_.cloneDeep(blankState));
   const [working, setWorking] = useState(false);
   const editing = !_.isNil(editCopy);
   const reviewList = useGetPOReviewDetails(showModal);
   const payload = {
      ItemNumber: selectedItem?.value,
      TypeOfSource: typeOfDataSource,
      PurchActivitySortDirection: purchActivitySortDirection,
      PurchActivitySortField: purchActivitySortField,
      VendorId: selectedItem?.vendorId,
   };

   useEffect(() => {
      refreshData();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [props.params]);

   function cancelAddReview() {
      setAddReview(false);
      setShowModal(_.cloneDeep(blankState));
   }

   function exportGrid() {
      setExport(true);
      api
         .postGetBlob(`Inventory/ExportPurchasingActivity`, payload)
         .then(response => {
            if (response && response.data) {
               if (response.data.success === false) {
                  if (_.isString(response?.data?.message)) {
                     toast.error(response?.data?.message);
                  }
                  return;
               }
               if (response.data) {
                  const XLSX_FILENAME = `PurchasingActivity.xlsx`
                  helpers.downloadExport(response.data, XLSX_FILENAME, constants.MIME_TYPES.xlsx)
               }
            } else {
               toast.error(response.message || "There was an error downloading the file");
            }
         }).catch((error) => {
            api.catchHandler(error)
            toast.error("There was an error downloading the file");
         })
         .finally(() => setExport(false));
   }

   function getValue(field, index, defaultValue = '') {
      const value = editing && editIndex === index
         ? editCopy[field]
         : data[index][field]
      return _.isNil(value)
         ? defaultValue
         : value
   }

   function onChange(value) {
      const copy = _.cloneDeep(showModal);
      _.set(copy, "blankPoReview.note", value);
      setShowModal(copy);
   }

   function onChangeFollowUp(value) {
      const copy = _.cloneDeep(showModal);
      _.set(copy, "blankPoReview.nextFollowUp", dateHelpers.formatDateForServer(value));
      setShowModal(copy);
   }

   function onSave() {
      setWorking(true);
      const payload = {
         dto: data[editIndex],
         newPromisedDate: editCopy.linePromisedDate,
         NewRequiredDate: editCopy.lineRequiredDate
      }
      api
         .post('Purchasing/UpdatePOLine', payload)
         .then(r => {
            if (r.data.success) {
               toast.success("Changes saved!");
               toggleEdit();
               refreshData();
            } else {
               toast.error(r.data.message);
               setWorking(false);
            }
         })
         .catch(err => {
            api.catchHandler(err)
            setWorking(false);
         })
   }

   function refreshData() {
      const cancelToken = axios.CancelToken;
      const source = cancelToken.source();

      if (props.params.selectedItem?.value !== params.selectedItem?.value) {
         setParams(props.params);
         setLoaded(false); //for the spinner
      }
      api
         .postS("Inventory/PurchasingActivity", payload, source.token)
         .then((response) => {
            if (response.data.success) {
               const dataResponse = response.data.itemGrid.purchasingActivity;
               setData(dataResponse);
               //set total here if its there
            } else {
               throw new Error(response);
            }
         })
         .catch(api.catchHandler)
         .finally(() => setLoaded(true));
      return () => {
         source.cancel("axios request cancelled");
      };
   }

   function saveReviewDetail() {
      const poReview = Object.assign({}, showModal.blankPoReview);
      const payload = {
         Dto: poReview,
         ItemNumber: showModal.datum?.itemNumber,
         PONumber: showModal.datum?.poNumber
      };

      if (payload.Dto.note.length < 1 || payload.Dto.note.length > 199) {
         toast.error("note is out of bounds, please try again...");
         return;
      }
      if (payload.Dto.nextFollowUp.length < 1) {
         payload.Dto.nextFollowUp = showModal.datum.nextReviewDate;
      }
      api
         .post("Purchasing/SavePOReviewDetail", payload)
         .then((response) => {
            if (response && response.data.success) {
               cancelAddReview();
               toast.success("note saved");
            } else {
               toast.error("Error saving review");
            }
         })
         .catch(api.catchHandler);
   }

   function toggleEdit(index) {
      if (editing) {
         setEditCopy(null);
         setEditIndex(null);
      } else {
         setEditCopy(_.cloneDeep(data[index]))
         setEditIndex(index);
      }
   }

   let alreadyDisplayedList = [];
   return (
      <>
         <Modal isOpen={showModal.showing} size="lg">
            <ModalHeader>Review PO Line Item</ModalHeader>
            <ModalBody>
               <Row>
                  <Col xs={3} sm={3}>PO Number:</Col>
                  <Col>{showModal.datum?.poNumber}</Col>
               </Row>
               <Row>
                  <Col xs={3} sm={3}>Item Number:</Col>
                  <Col>{showModal.datum?.itemNumber}</Col>
               </Row>
               <Row>
                  <Col xs={3} sm={3}>Orig Promised Date:</Col>
                  <Col>{dateHelpers.toLongDateString(showModal.datum?.lineOrigPromisedDate)}</Col>
               </Row>
               {helpers.hasRole(props.roles, [
                  constants.ROLE_IDS.Admin,
                  constants.ROLE_IDS.PowerUser,
                  constants.ROLE_IDS.PurchasingEdit,
               ]) ? (
                  <Row>
                     <Col xs={3} sm={3}>Next Follow Up:</Col>
                     <Col>{dateHelpers.toLongDateString(showModal.datum?.nextReviewDate)}</Col>
                  </Row>
               ) : null}
               <Row className="my-3 mx-1">
                  <ServiceThreadSpinner on={reviewList.working}>
                     <Table size="sm" className="data-table">
                        <thead>
                           <tr>
                              <th children={<span>Review<br />Date</span>} />
                              <th children={"Reviewed By"} />
                              <th children={<span>Next<br />Follow Up</span>} />
                              <th style={{ width: "28%" }} children={"Notes"} />
                           </tr>
                        </thead>
                        <tbody>
                           {reviewList.list.length ? (
                              _.map(reviewList.list, (datum, idx) => {
                                 return (
                                    <tr key={`123-${idx}`}>
                                       <td>{dateHelpers.toLongDateString(datum.dateCreated)}</td>
                                       <td>{datum.username}</td>
                                       <td>{dateHelpers.toLongDateString(datum.nextFollowUp)}</td>
                                       <td>{datum.note}</td>
                                    </tr>
                                 );
                              })
                           ) : (
                              <tr>
                                 <td style={{ textAlign: "center" }} colSpan={4}>
                                    No Reviews Found
                                 </td>
                              </tr>
                           )}
                        </tbody>
                     </Table>
                  </ServiceThreadSpinner>
               </Row>
               <Row>
                  <Col>
                     <Collapse isOpen={addReview}>
                        <Row>
                           <Col xs={6} className="pt-1 fw-bold text-end">Next Follow Up:</Col>
                           <Col xs={6}>
                              <Input
                                 type="date"
                                 className="w-50"
                                 value={showModal.blankPoReview.nextFollowUp}
                                 onChange={e => onChangeFollowUp(e.target.value)}
                              />
                           </Col>
                        </Row>
                        <Row>
                           <Col xs="1" sm="1">Notes:</Col>
                           <Col>
                              <Input
                                 type="text"
                                 maxLength="200"
                                 value={showModal.blankPoReview.note}
                                 onChange={(e) => onChange(e.target.value)}
                              />
                           </Col>
                        </Row>
                     </Collapse>
                  </Col>
               </Row>
            </ModalBody>
            <ModalFooter>
               {helpers.hasRole(props.roles, [
                  constants.ROLE_IDS.Admin,
                  constants.ROLE_IDS.PowerUser,
                  constants.ROLE_IDS.PurchasingEdit,
               ]) ? (
                  <Row>
                     <ButtonGroup>
                        <Button
                           children={addReview ? "Save Review" : "Add Review"}
                           className="button-success"
                           onClick={
                              addReview
                                 ? () => saveReviewDetail()
                                 : () => setAddReview(true)
                           }
                           disabled={false}
                        />
                        <Button
                           children="Cancel"
                           className="button-danger"
                           onClick={() => cancelAddReview()}
                        />
                     </ButtonGroup>
                  </Row>
               ) : (
                  <Button
                     children="Close"
                     className="button-danger"
                     onClick={() => cancelAddReview()}
                  />
               )}
            </ModalFooter>
         </Modal>
         <Card className="dashboard-card">
            <CardBody>
               <div>
                  <span className="h2 me-2">Purchasing Activity Last 12 Months</span>
               </div>
               <hr />
               <ServiceThreadSpinner on={!loaded}>
                  {loaded && data.length !== 0 ? (
                     <div>
                        <Row className={"mb-2"}>
                           <Col xs={{ offset: 6, size: 6 }} className="transparent-background">
                              <SpinnerButton
                                 title="Download as .xlsx"
                                 className="shredButtonExcel float-end ms-2"
                                 onClick={exportGrid}
                                 on={working || exporting}
                                 disabled={working || exporting}
                              ><FontAwesomeIcon icon="file-excel" size="lg" />
                              </SpinnerButton>
                           </Col>
                        </Row>
                        <Table size="sm" className="data-table">
                           <thead className="table-header">
                              <tr>
                                 <th style={{ width: "10%" }}>
                                    <SortHeader
                                       displayName={"Date"}
                                       field={"DocumentDate"}
                                       sortDirection={props.sortDirection}
                                       sorted={props.sortField === "DocumentDate"}
                                       callback={props.sortCallback}
                                       state={{
                                          purchActivitySortField: "DocumentDate",
                                          purchActivitySortDirection: props.sortDirection === "asc" ? "desc" : "asc",
                                          typeOfDataSource: constants.INVENTORYDASHBOARD_TYPES.PurchasingActivity,
                                       }}
                                    />
                                 </th>
                                 <th style={{ width: "10%" }}>
                                    <SortHeader
                                       displayName={"PO #"}
                                       field={"PONumber"}
                                       sortDirection={props.sortDirection}
                                       sorted={props.sortField === "PONumber"}
                                       callback={props.sortCallback}
                                       state={{
                                          purchActivitySortField: "PONumber",
                                          purchActivitySortDirection: props.sortDirection === "asc" ? "desc" : "asc",
                                          typeOfDataSource: constants.INVENTORYDASHBOARD_TYPES.PurchasingActivity,
                                       }}
                                    />
                                 </th>
                                 <th style={{ width: "8%" }}>
                                    <SortHeader
                                       displayName={"Ordered"}
                                       field={"OrderQty"}
                                       sortDirection={props.sortDirection}
                                       sorted={props.sortField === "OrderQty"}
                                       callback={props.sortCallback}
                                       state={{
                                          purchActivitySortField: "OrderQty",
                                          purchActivitySortDirection: props.sortDirection === "asc" ? "desc" : "asc",
                                          typeOfDataSource: constants.INVENTORYDASHBOARD_TYPES.PurchasingActivity,
                                       }}
                                    />
                                 </th>
                                 <th style={{ width: "5%" }}>
                                    <SortHeader
                                       displayName={<span>Unit<br />Cost</span>}
                                       field={"UnitCost"}
                                       sortDirection={props.sortDirection}
                                       sorted={props.sortField === "UnitCost"}
                                       callback={props.sortCallback}
                                       state={{
                                          purchActivitySortField: "UnitCost",
                                          purchActivitySortDirection: props.sortDirection === "asc" ? "desc" : "asc",
                                          typeOfDataSource: constants.INVENTORYDASHBOARD_TYPES.PurchasingActivity,
                                       }}
                                    />
                                 </th>
                                 <th style={{ width: "10%" }}>
                                    <SortHeader
                                       displayName={"Ext. Cost"}
                                       field={"ExtendedCost"}
                                       sortDirection={props.sortDirection}
                                       sorted={props.sortField === "ExtendedCost"}
                                       callback={props.sortCallback}
                                       state={{
                                          purchActivitySortField: "ExtendedCost",
                                          purchActivitySortDirection: props.sortDirection === "asc" ? "desc" : "asc",
                                          typeOfDataSource: constants.INVENTORYDASHBOARD_TYPES.PurchasingActivity,
                                       }}
                                    />
                                 </th>
                                 <th style={{ width: "10%", fontWeight: "500" }}>
                                    Received
                                 </th>
                                 <th style={{ width: "10%", fontWeight: "500" }}>
                                    Containers
                                 </th>
                                 <th style={{ width: "10%", fontWeight: "500" }}>
                                    <span>Received<br />Date</span>
                                 </th>
                                 <th style={{ width: "10%" }}>
                                    <SortHeader
                                       displayName={<span>Required<br />Date</span>}
                                       field={"LineRequiredDate"}
                                       sortDirection={props.sortDirection}
                                       sorted={props.sortField === "LineRequiredDate"}
                                       callback={props.sortCallback}
                                       state={{
                                          purchActivitySortField: "LineRequiredDate",
                                          purchActivitySortDirection: props.sortDirection === "asc" ? "desc" : "asc",
                                          typeOfDataSource: constants.INVENTORYDASHBOARD_TYPES.PurchasingActivity,
                                       }}
                                    />
                                 </th>
                                 <th style={{ width: "10%" }}>
                                    <SortHeader
                                       displayName={<span>Promised<br />Date</span>}
                                       field={"LinePromisedDate"}
                                       sortDirection={props.sortDirection}
                                       sorted={props.sortField === "LinePromisedDate"}
                                       callback={props.sortCallback}
                                       state={{
                                          purchActivitySortField: "LinePromisedDate",
                                          purchActivitySortDirection: props.sortDirection === "asc" ? "desc" : "asc",
                                          typeOfDataSource: constants.INVENTORYDASHBOARD_TYPES.PurchasingActivity,
                                       }}
                                    />
                                 </th>
                                 <th style={{ width: "10%" }}>
                                    <SortHeader
                                       displayName={"Status"}
                                       field={"POLineStatus"}
                                       sortDirection={props.sortDirection}
                                       sorted={props.sortField === "POLineStatus"}
                                       callback={props.sortCallback}
                                       state={{
                                          purchActivitySortField: "POLineStatus",
                                          purchActivitySortDirection: props.sortDirection === "asc" ? "desc" : "asc",
                                          typeOfDataSource: constants.INVENTORYDASHBOARD_TYPES.PurchasingActivity,
                                       }}
                                    />
                                 </th>
                                 <th style={{ width: "10%", fontWeight: "500" }}>
                                    <span>Landed<br />Cost</span>
                                 </th>
                                 <th style={{ width: "10%" }}>Review?</th>
                                 {helpers.hasRole(props.roles, [
                                    constants.ROLE_IDS.Admin,
                                    constants.ROLE_IDS.PowerUser,
                                    constants.ROLE_IDS.PurchasingEdit,
                                 ]) ? (
                                    <th width="3%"></th>
                                 ) : null}
                              </tr>
                           </thead>
                           <tbody>
                              {_.map(data, (datum, index) => {
                                 const alreadyDisplayed = !_.includes(alreadyDisplayedList, datum.poNumber);
                                 alreadyDisplayedList.push(datum.poNumber);
                                 return (
                                    <tr key={index} className={`tr-${index % 2 === 0 ? "primary" : "secondary"}`}>
                                       <td className="data-cell" children={dateHelpers.toLongDateString(datum.documentDate)} />
                                       <PODropdownMenu
                                          poNumber={datum.poNumber}
                                          itemNumber={datum.itemNumber}
                                          attachmentCount={datum.poAttachmentCount}
                                          showAddContainer={currentUser?.isAdmin || currentUser?.isPowerUser || currentUser?.isPurchasingEdit}
                                          showAddCost={currentUser?.isAdmin || currentUser?.isPowerUser || currentUser?.isPurchasingEdit}
                                          containers={datum.containers}
                                          callbackRefresh={() => refreshData()}
                                       />
                                       <td className="data-cell" children={Math.round(datum.orderQty).toLocaleString()} />
                                       <td className="data-cell" children={helpers.formatCurrency(datum.unitCost)} />
                                       <td className="data-cell" children={helpers.formatCurrency(datum.extendedCost)} />
                                       <td className="data-cell" children={Math.round(datum.qtyShipped).toLocaleString()} />
                                       <td className="data-cell" children={datum.containers} />
                                       {dateHelpers.toLongDateString(datum.receiptDate) === '01/01/0001' ? (
                                          <td>{""}</td>
                                       ) : (
                                          <td className="data-cell" children={dateHelpers.toLongDateString(datum.receiptDate)} />
                                       )}
                                       {editIndex !== index ?
                                          <td>{dateHelpers.toLongDateString(getValue("lineRequiredDate", index))}</td> :
                                          <td className="px-0">
                                             <Input
                                                type="date"
                                                className={classNames("px-1 w-80", { "readonly-input": editIndex !== index })}
                                                value={dateHelpers.formatDateForServer(getValue("lineRequiredDate", index))}
                                                onChange={e => setEditCopy({ ...editCopy, lineRequiredDate: e.target.value })}
                                                readOnly={editIndex !== index}
                                             />
                                          </td>
                                       }
                                       {editIndex !== index ?
                                          <td>{dateHelpers.toLongDateString(getValue("linePromisedDate", index))}</td> :
                                          <td className="px-0">
                                             <Input
                                                type="date"
                                                className={classNames("px-1 w-80", { "readonly-input": editIndex !== index })}
                                                value={dateHelpers.formatDateForServer(getValue("linePromisedDate", index))}
                                                onChange={e => setEditCopy({ ...editCopy, linePromisedDate: e.target.value })}
                                                readOnly={editIndex !== index}
                                             />
                                          </td>
                                       }
                                       <td className="data-cell" children={datum.lineStatusText} />
                                       <td className="data-cell" children={helpers.formatCurrency(datum.landedCost)} />
                                       {alreadyDisplayed ? (
                                          <td
                                             className="data-cell"
                                             children={
                                                <span
                                                   onClick={() => {
                                                      var dt = new Date(datum.nextReviewDate).toISOString().substring(0, 10);
                                                      setShowModal({
                                                         datum: datum,
                                                         showing: true,
                                                         blankPoReview: {
                                                            ...showModal.blankPoReview,
                                                            nextFollowUp: dt ?? "",
                                                            reviewId: datum.reviewId,
                                                         },
                                                      })
                                                   }
                                                   }
                                                >
                                                   {datum.totalPOReviews > 0 ? (
                                                      <FaFlag
                                                         size={25}
                                                         style={{ color: "#f76f1e", cursor: "pointer", }}
                                                      />
                                                   ) : (
                                                      <FaFlag
                                                         className="p-1"
                                                         size={25}
                                                         style={{ color: "#ffffff", cursor: "pointer", backgroundColor: "#f76f1e", }}
                                                      />
                                                   )}
                                                </span>
                                             }
                                          />
                                       ) : (
                                          <td>{""}</td>
                                       )}
                                       {helpers.hasRole(props.roles, [
                                          constants.ROLE_IDS.Admin,
                                          constants.ROLE_IDS.PowerUser,
                                          constants.ROLE_IDS.PurchasingEdit,
                                       ]) ? (
                                          <td className="px-0">
                                             <Button
                                                className="fw-bold shredLinkButton "
                                                onClick={editing && editIndex === index ? onSave : () => toggleEdit(index)}
                                                disabled={working || (editing && editIndex !== index)}
                                             >
                                                {editing && editIndex === index ? "Update" : "Edit"}
                                             </Button>
                                             <Button
                                                className={classNames("fw-bold shredLinkButton", { "invisible": !editing || editIndex !== index })}
                                                onClick={() => toggleEdit(index)}
                                                disabled={working || (editing && editIndex !== index)}
                                             >
                                                Cancel
                                             </Button>
                                          </td>
                                       ) : null}
                                    </tr>
                                 );
                              })}
                           </tbody>
                           <tfoot className="table-footer">
                              <tr></tr>
                           </tfoot>
                        </Table>
                     </div>
                  ) : (
                     <div>
                        <span>No purchasing activity found for the period</span>
                     </div>
                  )}
               </ServiceThreadSpinner>
            </CardBody>
         </Card>
      </>
   );
}
